import React, { useState, useEffect } from 'react'; // Add useEffect
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup, Spinner } from 'react-bootstrap'; // Add Spinner
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaPlus, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaLock } from 'react-icons/fa';
import Select from 'react-select';
import './AddDSO.css'; // Reusing the same CSS
import { useNavigate } from 'react-router-dom';
import { rmApi } from '../../services/api.js'; // Import rmApi specifically
import { locationService } from '../../services/location.js'; // Import locationService

const AddRM = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Add loading state for submit
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    state_id: '', // Use state_id
    city_id: '',  // Use city_id
    pincode_id: '', // Use pincode_id
    password: '',
    confirmPassword: ''
  });
  
  const [validated, setValidated] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState('');

  // State for dynamic dropdowns
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [isFetchingStates, setIsFetchingStates] = useState(false);
  const [isFetchingCities, setIsFetchingCities] = useState(false);
  const [isFetchingPincodes, setIsFetchingPincodes] = useState(false);

  // Fetch states on component mount
  useEffect(() => {
    const fetchInitialStates = async () => {
      setIsFetchingStates(true);
      try {
        // Assuming getAllStates returns { data: { states: [{id: 1, name: 'StateA', code: 'SA'}, ...] } }
        // Adjust based on your actual API response structure in location.js
        const response = await locationService.getAllStates({ limit: 100 }); // Fetch all states
        // Ensure the response structure matches expectations
        const stateOptions = response.data?.states?.map(s => ({ value: s.id, label: s.name })) || [];
        setStates(stateOptions);
      } catch (error) {
        console.error("Failed to fetch states:", error);
        setErrorMessage('Failed to load states.');
        setShowError(true);
      } finally {
        setIsFetchingStates(false);
      }
    };
    fetchInitialStates();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Handle State Change - Fetch Cities
  const handleStateChange = async (selectedOption) => {
    const stateId = selectedOption ? selectedOption.value : '';
    setFormData(prev => ({
      ...prev,
      state_id: stateId,
      city_id: '', // Reset city
      pincode_id: '' // Reset pincode
    }));
    setCities([]); // Clear cities dropdown
    setPincodes([]); // Clear pincodes dropdown

    if (stateId) {
      setIsFetchingCities(true);
      try {
        // Assuming getCitiesByState returns { data: { cities: [{id: 10, name: 'CityA'}, ...] } }
        const response = await locationService.getCitiesByState(stateId, { limit: 100 }); // Fetch all cities for state
        const cityOptions = response.data?.cities?.map(c => ({ value: c.id, label: c.name })) || [];
        setCities(cityOptions);
      } catch (error) {
        console.error("Failed to fetch cities:", error);
        setErrorMessage('Failed to load cities for the selected state.');
        setShowError(true);
      } finally {
        setIsFetchingCities(false);
      }
    }
  };

  // Handle City Change - Fetch Pincodes
  const handleCityChange = async (selectedOption) => {
    const cityId = selectedOption ? selectedOption.value : '';
    setFormData(prev => ({
      ...prev,
      city_id: cityId,
      pincode_id: '' // Reset pincode
    }));
    setPincodes([]); // Clear pincodes dropdown

    if (cityId) {
      setIsFetchingPincodes(true);
      try {
        // Assuming getPincodesByCity returns { data: { pincodes: [{id: 100, code: '12345'}, ...] } }
        const response = await locationService.getPincodesByCity(cityId, { limit: 100 }); // Fetch all pincodes for city
        const pincodeOptions = response.data?.pincodes?.map(p => ({ value: p.id, label: p.code })) || [];
        setPincodes(pincodeOptions);
      } catch (error) {
        console.error("Failed to fetch pincodes:", error);
        setErrorMessage('Failed to load pincodes for the selected city.');
        setShowError(true);
      } finally {
        setIsFetchingPincodes(false);
      }
    }
  };

  // Handle Pincode Change
  const handlePincodeChange = (selectedOption) => {
      setFormData(prev => ({
          ...prev,
          pincode_id: selectedOption ? selectedOption.value : ''
      }));
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Check password strength when password field changes
    if (name === 'password') {
      checkPasswordStrength(value);
    }
    
    // Check if passwords match when either password field changes
    if (name === 'password' || name === 'confirmPassword') {
      if (name === 'password') {
        setPasswordMatch(value === formData.confirmPassword);
      } else {
        setPasswordMatch(formData.password === value);
      }
    }
  };

  // Check password strength
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 5) strength += 1;
    
    // Contains uppercase letters
    if (password.match(/[A-Z]/)) strength += 1;
    
    // Contains lowercase letters
    if (password.match(/[a-z]/)) strength += 1;
    
    // Contains numbers
    if (password.match(/[0-9]/)) strength += 1;
    
    switch (strength) {
      case 0:
      case 1:
        setPasswordStrength('weak');
        break;
      case 2:
        setPasswordStrength('medium');
        break;
      case 3:
      case 4:
        setPasswordStrength('strong');
        break;
      default:
        setPasswordStrength('');
    }
  };
  
  // Get password strength color
  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'strong':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const handleSubmit = async (e, addAnother = false) => { // Make async
    e.preventDefault();
    const form = e.currentTarget;
    setShowError(false); // Reset error on new submission attempt
    setErrorMessage('');

    // Extra validation for password match
    if (formData.password !== formData.confirmPassword) {
      setPasswordMatch(false);
      setValidated(true);
      return;
    }
    else {
      setPasswordMatch(true); // Ensure match is true if they are equal
    }

    // Check form validity using Bootstrap's mechanism AND check location IDs
    if (form.checkValidity() === false || !formData.state_id || !formData.city_id || !formData.pincode_id) {
      e.stopPropagation();
      setValidated(true);
      // Manually check location fields as react-select doesn't integrate with checkValidity well
      if (!formData.state_id || !formData.city_id || !formData.pincode_id) {
          console.log("Location fields validation failed");
          // Optionally set a specific error message for location fields
          setErrorMessage('Please select State, City, and Pincode.');
          setShowError(true);
      }
      return;
    }

    setValidated(true); // Keep showing validation state after check
    setIsLoading(true); // Start loading indicator

    // Prepare data for API (ensure field names match backend RMCreate schema)
    const apiData = {
      name: formData.name,
      email: formData.email,
      mobile: formData.phone, // Backend expects 'mobile'
      password: formData.password,
      state_id: formData.state_id,
      city_id: formData.city_id,
      pincode_id: formData.pincode_id,
    };

    console.log('Submitting RM data:', apiData); // Log data being sent

    try {
      // Make the actual API call
      await rmApi.createRelationshipManager(apiData);
      setShowSuccess(true);

      if (addAnother) {
        // Reset form for adding another RM
        setFormData({
          name: '', email: '', phone: '',
          state_id: '', city_id: '', pincode_id: '',
          password: '', confirmPassword: ''
        });
        setValidated(false);
        setPasswordStrength('');
        setPasswordMatch(true);
        setCities([]); // Clear dependent dropdowns
        setPincodes([]);
        // Note: States are fetched once on mount, not resetting them here.

        // Clear success message after a short delay
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        // Redirect after success message shows for a bit
        setTimeout(() => navigate('/admin/rm-list'), 2000);
      }
    } catch (error) {
      console.error("Failed to add RM:", error);
      let message = 'Failed to add RM. Please check the details and try again.'; // Default message

      // Check for specific error status codes or details
      if (error.response) {
        if (error.response.status === 409) {
          // Specific message for duplicate entry (409 Conflict)
          message = error.response.data?.detail || "An RM with this email or mobile number already exists.";
        } else {
          // Use detail from other errors if available
          message = error.response.data?.detail || message;
        }
      }
      // If no response object, the error might be network-related or client-side

      setShowError(true);
      setErrorMessage(message);
    } finally {
      setIsLoading(false); // Stop loading indicator
    }
  };
  
  return (
    <Container fluid className="p-3">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1">
            <FaArrowLeft 
              className="me-2 cursor-pointer" 
              onClick={() => navigate('/admin/rm-list')}
              style={{ cursor: 'pointer' }}
            />
            Add New Relationship Manager
          </h4>
          <p className="text-muted small mb-0">Create a new relationship manager in the system</p>
        </Col>
      </Row>
      
      {/* Success and Error Alerts */}
      {showSuccess && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
              Relationship Manager added successfully!
            </Alert>
          </Col>
        </Row>
      )}
      
      {showError && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      
      {/* Form */}
      <Card className="shadow-sm">
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={(e) => handleSubmit(e)} className="compact-form">
            
            {/* Two Column Layout for the Form */}
            <Row className="g-3">
              <Col md={6}>
                {/* Personal Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaUser className="me-2" /> Personal Information
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaUser /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a name.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaPhone /></InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number (10 digits)"
                      pattern="[0-9]{10}"
                      required
                      maxLength="10" // <-- Add this line
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid 10-digit phone number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>


                {/* Location Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaMapMarkerAlt className="me-2" /> Location
                </h6>
                
                <Form.Group className="mb-2">
                  <Form.Label>State <span className="text-danger">*</span></Form.Label>
                  <Select
                    name="state_id" // Match state key
                    value={states.find(option => option.value === formData.state_id) || null} // Find object by ID
                    onChange={handleStateChange} // Pass handler directly
                    options={states} // Use the states array directly
                    placeholder="Select State"
                    isLoading={isFetchingStates} // Add loading indicator
                    // className={validated && !formData.state_id ? 'is-invalid' : ''} // react-select handles styling, use validation below
                    classNamePrefix="react-select"
                    isClearable={true} // Allow clearing
                    required // Add required attribute for basic check
                  />
                  {/* Use Bootstrap's invalid feedback style */}
                  {validated && !formData.state_id && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      Please select a state.
                    </div>
                  )}
                </Form.Group>
                
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>City <span className="text-danger">*</span></Form.Label>
                      <Select
                        name="city_id" // Match state key
                        value={cities.find(option => option.value === formData.city_id) || null} // Find object by ID
                        onChange={handleCityChange} // Pass handler directly
                        options={cities} // Use the cities array directly
                        placeholder="Select City"
                        isLoading={isFetchingCities} // Add loading indicator
                        isDisabled={!formData.state_id || isFetchingCities} // Disable if no state or loading
                        // className={validated && !formData.city_id ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable={true}
                        required
                      />
                      {validated && !formData.city_id && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          Please select a city.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Pincode <span className="text-danger">*</span></Form.Label>
                      <Select
                        name="pincode_id" // Match state key
                        value={pincodes.find(option => option.value === formData.pincode_id) || null} // Find object by ID
                        onChange={handlePincodeChange} // Pass handler directly
                        options={pincodes} // Use the pincodes array directly
                        placeholder="Select Pincode"
                        isLoading={isFetchingPincodes} // Add loading indicator
                        isDisabled={!formData.city_id || isFetchingPincodes} // Disable if no city or loading
                        // className={validated && !formData.pincode_id ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable={true}
                        required
                      />
                      {validated && !formData.pincode_id && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          Please select a pincode.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              
              <Col md={6}>
                {/* Account Security Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaLock className="me-2" /> Account Security
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Create Password <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create password"
                      minLength="5" // Use minLength attribute for simple length check
                      required
                      aria-describedby="passwordHelpBlock"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      Password must be at least 5 characters long.
                    </Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text id="passwordHelpBlock" muted>
                    Password strength: {passwordStrength && (
                      <span className={`text-${getPasswordStrengthColor()} fw-bold`}>
                        {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                      </span>
                    )}
                  </Form.Text>
                  <div className="password-checklist">
                    <div className={`d-flex align-items-center ${formData.password && formData.password.length >= 5 ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least 5 characters
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[A-Z]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one uppercase letter
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[a-z]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one lowercase letter
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[0-9]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one number
                    </div>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      required
                      isInvalid={validated && !passwordMatch}
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      Passwords do not match.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-3" />
            
            <Row>
              <Col className="d-flex gap-2 action-buttons">
               <Button
                 variant="primary"
                 type="submit"
                 size="sm"
                 disabled={isLoading} // Disable when loading
               >
                 {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> : <FaSave className="me-1" />}
                 {isLoading ? 'Saving...' : 'Save RM'}
               </Button>

               <Button
                 variant="outline-primary"
                 onClick={(e) => handleSubmit(e, true)}
                 size="sm"
                 disabled={isLoading} // Disable when loading
               >
                 {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> : <FaPlus className="me-1" />}
                 {isLoading ? 'Saving...' : 'Save & Add Another'}
               </Button>

               <Button
                 variant="outline-secondary"
                 onClick={() => navigate('/admin/rm-list')}
                 size="sm"
                 disabled={isLoading} // Disable cancel during save? Optional.
               >
                 Cancel
               </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddRM;
